"use client";

import { useEffect, useMemo, useState } from "react";
import type { StyleSpecification } from "maplibre-gl";
import { useAppearanceStore } from "@/store/appearance.store";
import { useLocaleStore } from "@/store/locale.store";
import { MAP_ASSETS_URL, MAP_COUNTRY_MASK_URL } from "./provider";

/**
 * Styles de carte Fast — les mêmes fichiers que l'app client et la console :
 * jour / nuit, libellés FR ou AR, glyphes BDO Grotesk.
 *
 * Chargés depuis l'hébergement commun (`NEXT_PUBLIC_MAP_ASSETS_URL/styles/…`)
 * avec repli sur la copie embarquée dans `public/maps/`. Le repli compte : sans
 * lui, une coupure de `map-assets` laisserait des cartes blanches sur le suivi
 * de commande.
 */
export type StyleVariant = "fast-light" | "fast-dark" | "fast-light-ar" | "fast-dark-ar";

export function styleVariant(isDark: boolean, locale?: string | null): StyleVariant {
  const ar = (locale ?? "").toLowerCase().startsWith("ar");
  return `${isDark ? "fast-dark" : "fast-light"}${ar ? "-ar" : ""}` as StyleVariant;
}

const cache = new Map<StyleVariant, Promise<StyleSpecification>>();

export const COUNTRY_MASK_LAYER_ID = "country-mask";
export const COUNTRY_MASK_SOURCE_ID = "country-mask";

/**
 * Limite la carte au pays : un remplissage de la couleur du fond sur tout le
 * monde sauf l'Algérie (le trou), plus un liseré d'un pixel sur la frontière.
 * Ajouté au SOMMET du style, donc au-dessus des libellés — les marqueurs et
 * tracés que l'on pose ensuite sont en Algérie, dans le trou, et ne sont pas
 * masqués.
 */
function applyCountryMask(style: StyleSpecification, isDark: boolean): StyleSpecification {
  const background = style.layers.find((l) => l.type === "background");
  const paint = (background && "paint" in background ? background.paint : undefined) as
    | { "background-color"?: unknown }
    | undefined;
  const bg =
    typeof paint?.["background-color"] === "string"
      ? paint["background-color"]
      : isDark
        ? "#383C44"
        : "#F2F2F0";
  const border = isDark ? "#4A4F58" : "#D8D8D4";

  return {
    ...style,
    sources: {
      ...style.sources,
      [COUNTRY_MASK_SOURCE_ID]: { type: "geojson", data: MAP_COUNTRY_MASK_URL },
    },
    layers: [
      ...style.layers,
      {
        id: COUNTRY_MASK_LAYER_ID,
        type: "fill",
        source: COUNTRY_MASK_SOURCE_ID,
        paint: { "fill-color": bg, "fill-opacity": 1 },
      },
      {
        id: `${COUNTRY_MASK_LAYER_ID}-line`,
        type: "line",
        source: COUNTRY_MASK_SOURCE_ID,
        paint: { "line-color": border, "line-width": 1 },
      },
    ],
  };
}

async function fetchStyle(url: string): Promise<StyleSpecification> {
  const res = await fetch(url, { cache: "force-cache" });
  if (!res.ok) throw new Error(`style ${res.status}`);
  return (await res.json()) as StyleSpecification;
}

export function loadMapStyle(variant: StyleVariant): Promise<StyleSpecification> {
  let p = cache.get(variant);
  if (!p) {
    p = fetchStyle(`${MAP_ASSETS_URL}/styles/${variant}.json`)
      .catch(() => fetchStyle(`/maps/${variant}.json`))
      .then((style) => {
        const s = { ...style } as StyleSpecification & { glyphs?: string };
        // Les fichiers portent un placeholder pour la base des glyphes : sans
        // substitution, MapLibre demanderait « GLYPHS_BASE_URL/… » et les
        // libellés ne s'afficheraient pas du tout.
        if (s.glyphs?.includes("GLYPHS_BASE_URL")) {
          s.glyphs = s.glyphs.replace("GLYPHS_BASE_URL", `${MAP_ASSETS_URL}/fonts`);
        }
        return applyCountryMask(s, variant.startsWith("fast-dark"));
      });
    cache.set(variant, p);
    p.catch(() => cache.delete(variant));
  }
  return p;
}

/**
 * Style prêt à passer à `<Map mapStyle>`, suivant le thème et la langue.
 * `null` tant qu'il n'est pas chargé — les écrans affichent alors leur
 * squelette plutôt qu'une carte à moitié peinte.
 */
export function useMapStyle(): StyleSpecification | null {
  const mode = useAppearanceStore((s) => s.mode);
  const locale = useLocaleStore((s) => s.locale);
  const variant = useMemo(() => styleVariant(mode === "dark", locale), [mode, locale]);
  const [style, setStyle] = useState<StyleSpecification | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadMapStyle(variant)
      .then((s) => !cancelled && setStyle(s))
      .catch(() => !cancelled && setStyle(null));
    return () => {
      cancelled = true;
    };
  }, [variant]);

  return style;
}

/** Couleurs de tracé cohérentes avec l'app client (façon Uber). */
export const ROUTE_COLORS = {
  light: { line: "#111214", casing: "#FFFFFF", walk: "#5F646B" },
  dark: { line: "#F4F5F7", casing: "#2B2F36", walk: "#C9CDD3" },
} as const;
